import { type AppRole } from '@craft/database';
import { type NonEmptyArray, type Prettify } from '@craft/lib';

import { type FeatureFlagControl } from '@/src/integrations/statsig';
import { type ActivePageMatcher } from '@/src/lib/pages';
import {
  type Auth0AuthenticatedPageId,
  type EdgeSafePage,
  type EdgeSafePageId,
  EdgeSafePages,
  isLtiPageId,
  isPublicPageId,
  type LtiPageId,
  type PublicPageId,
} from '@/src/middleware/pages';

import { type PageIntegration, type PageLayoutVersion } from './types';

interface BasePageInput {
  /**
   * Defines the version of the page layout to be used for the page. This is being used for
   * purposes of supporting the rollout of new page layouts incrementally.
   *
   * @see {@link PageLayoutVersion}
   */
  readonly layoutVersion: PageLayoutVersion;
  readonly title: null | string;
}

interface PublicPageInput extends BasePageInput {
  readonly integrations: null;
}

interface BaseAuthPageInput extends BasePageInput {
  /**
   * Defines an optional {@link FeatureFlagControl} that gates access to the page based on the
   * state of a feature flag for the authenticated user. When provided, the page's
   * `getServerSideProps` will automatically evaluate the flag and prevent the page from rendering
   * if the user does not satisfy the gate.
   *
   * A value of `null` does not mean that any user can access the page. It only means that no
   * feature-flag check is performed automatically by the page's `getServerSideProps`. Other
   * authorization layers (route guards, middleware, server-side authorization in resolvers, etc.)
   * still apply.
   */
  readonly featureFlag: FeatureFlagControl | null;
  readonly isRenderedInShell?: boolean;
  /**
   * Defines an optional, non-empty array of {@link AppRole}s that the authenticated user's
   * selected role must belong to in order to access the page. When provided, the page's
   * `getServerSideProps` will automatically reject the request if the selected role is not in the
   * list.
   *
   * A value of `null` does not mean that any user can access the page. It only means that no
   * role-based check is performed automatically by the page's `getServerSideProps`. Other
   * authorization layers (route guards, middleware, server-side authorization in resolvers, etc.)
   * still apply.
   */
  readonly roles: NonEmptyArray<AppRole> | null;
}

interface LtiPageInput extends BaseAuthPageInput {
  readonly integrations: null;
}

interface Auth0AuthenticatedPageInput extends BaseAuthPageInput {
  /**
   * Defines the conditions under which the page is considered active based on the current
   * pathname. This is used to determine which page(s) should be considered active for a given
   * pathname, which can be useful for purposes of things like setting the active state of
   * navigation links, etc.
   *
   * This option is currently only applicable for authenticated pages as public pages currently
   * do not have in-app navigation links that would require active state based on the current
   * pathname.
   */
  readonly activeMatcher: ActivePageMatcher | null;
  readonly integrations: NonEmptyArray<PageIntegration> | null;
}

type PageInput<I extends EdgeSafePageId> = I extends PublicPageId
  ? PublicPageInput
  : I extends LtiPageId
    ? LtiPageInput
    : I extends Auth0AuthenticatedPageId
      ? Auth0AuthenticatedPageInput
      : never;

type WithPageAndId<Id extends EdgeSafePageId, T> = Id extends EdgeSafePageId
  ? Prettify<{ readonly id: Id } & EdgeSafePage<Id> & T>
  : never;

type PageOutput<Id extends EdgeSafePageId, T extends PageInput<Id>> = Id extends LtiPageId
  ? WithPageAndId<
      Id,
      {
        readonly activeMatcher: null;
        readonly id: Id;
        readonly isRenderedInShell: boolean;
        readonly roles: null;
      } & T
    >
  : Id extends PublicPageId
    ? WithPageAndId<
        Id,
        {
          readonly activeMatcher: null;
          readonly id: Id;
          readonly isRenderedInShell: false;
          readonly roles: null;
        } & T
      >
    : Id extends Auth0AuthenticatedPageId
      ? WithPageAndId<
          Id,
          {
            readonly activeMatcher: ActivePageMatcher | null;
            readonly id: Id;
            readonly isRenderedInShell: boolean;
            readonly roles: NonEmptyArray<AppRole> | null;
          } & T
        >
      : never;

/**
 * Closed discriminated union used as the {@link Page} implementation signature so that narrowing
 * `args[0]` slides the whole tuple to one variant, propagating the correlated `input` shape.
 *
 * The Auth0 branch excludes {@link PublicPageId} because {@link Auth0AuthenticatedPageId} is
 * declared as `Exclude<EdgeSafePageId, LtiPageId>` and therefore overlaps with {@link PublicPageId}
 * at the type level, even though the runtime `auth` field partitions them.
 */
type PageArgs =
  | readonly [
      id: Exclude<Auth0AuthenticatedPageId, PublicPageId>,
      input: Auth0AuthenticatedPageInput,
    ]
  | readonly [id: LtiPageId, input: LtiPageInput]
  | readonly [id: PublicPageId, input: PublicPageInput];

const isLtiArgs = (args: PageArgs): args is readonly [LtiPageId, LtiPageInput] =>
  isLtiPageId(args[0]);

const isPublicArgs = (args: PageArgs): args is readonly [PublicPageId, PublicPageInput] =>
  isPublicPageId(args[0]);

export function Page<Id extends LtiPageId, Input extends PageInput<Id>>(
  id: Id,
  input: Input,
): PageOutput<Id, Input>;

export function Page<Id extends PublicPageId, Input extends PageInput<Id>>(
  id: Id,
  input: Input,
): PageOutput<Id, Input>;

export function Page<
  Id extends Exclude<Auth0AuthenticatedPageId, PublicPageId>,
  Input extends PageInput<Id>,
>(id: Id, input: Input): PageOutput<Id, Input>;

export function Page(...args: PageArgs) {
  if (isLtiArgs(args)) {
    const [id, input] = args;

    id satisfies LtiPageId;
    input satisfies LtiPageInput;

    return {
      ...EdgeSafePages.get(id),
      ...input,
      activeMatcher: null,
      integrations: null,
      isRenderedInShell: input.isRenderedInShell ?? true,
      roles: input.roles ?? null,
    };
  } else if (isPublicArgs(args)) {
    const [id, input] = args;

    id satisfies PublicPageId;
    input satisfies PublicPageInput;

    const output: PageOutput<PublicPageId, PublicPageInput> = {
      ...EdgeSafePages.get(id),
      ...input,
      activeMatcher: null,
      integrations: null,
      isRenderedInShell: false,
      roles: null,
    };
    return output;
  }
  const [id, input] = args;

  id satisfies Auth0AuthenticatedPageId;
  input satisfies Auth0AuthenticatedPageInput;

  return {
    ...EdgeSafePages.get(id),
    ...input,
    activeMatcher: input.activeMatcher ?? null,
    isRenderedInShell: input.isRenderedInShell ?? true,
    roles: input.roles ?? null,
  };
}
